import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { getOwnBusiness } from '../api/endpoints/businesses';
import { updateSchedule } from '../api/endpoints/schedule';
import { BusinessDetailSchema, type BusinessDetail, type ScheduleDay } from '../api/schemas';
import { useSession } from '../auth/useSession';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { WEEKDAYS, WEEKDAY_LABEL, hoursToScheduleDays, toHourClock } from '../lib/schedule';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import { useVersionConflict } from '../lib/use-version-conflict';
import { isVersionConflict } from '../lib/version-conflict';

const HOUR_OPTIONS = Array.from({ length: 25 }, (_unused, hour) => toHourClock(hour));

/**
 * The week as the version check compares it: one value per day, so a clash
 * reads "Lunes: tú 8:00 a 17:00, guardado 9:00 a 18:00" and a change to
 * another day than the one this person touched simply merges.
 */
type DayValue = { isClosed: boolean; opensAt: string | null; closesAt: string | null };
type DayKey = `d${(typeof WEEKDAYS)[number]}`;
type WeekDraft = Record<DayKey, DayValue>;
const DAY_KEYS = WEEKDAYS.map((day) => `d${day}` as DayKey);

function toWeekDraft(rows: ScheduleDay[]): WeekDraft {
  return Object.fromEntries(WEEKDAYS.map((day) => {
    const row = rows.find((item) => item.dayOfWeek === day);
    const closed = !row || row.isClosed === true;
    return [`d${day}`, closed ? { isClosed: true, opensAt: null, closesAt: null }
      : { isClosed: false, opensAt: row.opensAt ?? null, closesAt: row.closesAt ?? null }];
  })) as WeekDraft;
}

function fromWeekDraft(draft: WeekDraft): ScheduleDay[] {
  return WEEKDAYS.map((dayOfWeek) => {
    const value = draft[`d${dayOfWeek}`];
    if (value.isClosed) return { dayOfWeek, isClosed: true };
    return { dayOfWeek, ...(value.opensAt ? { opensAt: value.opensAt } : {}), ...(value.closesAt ? { closesAt: value.closesAt } : {}) };
  });
}

function showDay(value: DayValue): string {
  return value.isClosed ? 'Cerrado' : `${value.opensAt ?? 'sin hora'} a ${value.closesAt ?? 'sin hora'}`;
}

function parseBusinessRecord(current: unknown): BusinessDetail | null {
  const parsed = BusinessDetailSchema.safeParse(current);
  return parsed.success ? parsed.data : null;
}

export function AgendaPage() {
  const { session } = useSession();
  const businessId = session?.businessId ?? '';
  const queryClient = useQueryClient();

  const businessQuery = useQuery({
    queryKey: ['business-detail', businessId],
    queryFn: getOwnBusiness,
    enabled: Boolean(businessId),
  });

  const [rows, setRows] = useState<ScheduleDay[] | undefined>();
  // The week this person started from and its scheduleVersion. Seeded once: a
  // newer copy arriving in the background never replaces what is being typed;
  // a real change by someone else shows up as a version_conflict on save.
  const start = useRef<{ draft: WeekDraft; version: number | undefined } | null>(null);

  useEffect(() => {
    if (!businessQuery.data) return;
    const loaded = hoursToScheduleDays(businessQuery.data.hours);
    start.current ??= { draft: toWeekDraft(loaded), version: businessQuery.data.scheduleVersion };
    setRows((current) => current ?? loaded);
  }, [businessQuery.data]);

  function startFrom(record: BusinessDetail) {
    start.current = { draft: toWeekDraft(hoursToScheduleDays(record.hours)), version: record.scheduleVersion };
  }

  /** The saved week goes straight into the cache: a read right after a write can come back old. */
  function keepSaved(record: Partial<BusinessDetail>) {
    queryClient.setQueryData<BusinessDetail | undefined>(
      ['business-detail', businessId],
      (previous) => (previous ? { ...previous, ...record } : previous),
    );
  }

  const saveMutation = useMutationAttempt({
    mutationFn: (input: { hours: ScheduleDay[]; expectedVersion?: number }, key: string) =>
      updateSchedule({ businessId, ...input }, key),
    onSuccess: (business) => {
      keepSaved(business);
      setRows(hoursToScheduleDays(business.hours));
      startFrom(business as BusinessDetail);
    },
    onError: (error, input) => {
      if (start.current) conflict.handle(error, { base: start.current.draft, mine: toWeekDraft(input.hours) });
    },
  });

  const conflict = useVersionConflict<BusinessDetail, WeekDraft>({
    keys: DAY_KEYS,
    labels: Object.fromEntries(WEEKDAYS.map((day) => [`d${day}`, WEEKDAY_LABEL[day]])) as Record<DayKey, string>,
    format: Object.fromEntries(DAY_KEYS.map((key) => [key, showDay])) as Record<DayKey, (value: DayValue) => string>,
    parse: parseBusinessRecord,
    toDraft: (record) => toWeekDraft(hoursToScheduleDays(record.hours)),
    rebase: ({ draft, record }) => {
      setRows(fromWeekDraft(draft));
      startFrom(record);
    },
    save: ({ draft, version }) => saveMutation.mutate({ hours: fromWeekDraft(draft), expectedVersion: version }),
    current: () => (rows ? toWeekDraft(rows) : null),
    discard: (record) => {
      keepSaved(record);
      setRows(hoursToScheduleDays(record.hours));
      startFrom(record);
    },
  });

  function updateRow(dayOfWeek: number, patch: Partial<ScheduleDay>) {
    setRows((current) =>
      current?.map((row) => (row.dayOfWeek === dayOfWeek ? { ...row, ...patch } : row)),
    );
  }

  return (
    <main>
      <PageHeader title="Horario" description="El horario semanal de tu negocio." />
      {businessQuery.isLoading ? <LoadingState label="Cargando horario…" /> : null}
      {businessQuery.isError ? <ErrorBanner message={errorMessage(businessQuery.error)} /> : null}
      {saveMutation.isError && !isVersionConflict(saveMutation.error) ? <ErrorBanner message={errorMessage(saveMutation.error)} /> : null}
      {saveMutation.isSuccess && !conflict.asking ? (
        <p role="status" className="notice">
          Horario guardado.
        </p>
      ) : null}
      {rows ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (saveMutation.uncertain) { saveMutation.retry(); return; }
            conflict.clear();
            saveMutation.mutate({ hours: rows, ...(start.current?.version !== undefined ? { expectedVersion: start.current.version } : {}) });
          }}
        >
          <div className="table-scroll">
            <table className="agenda-table">
            <tbody>
              {WEEKDAYS.map((dayOfWeek) => {
                const row = rows.find((item) => item.dayOfWeek === dayOfWeek);
                if (!row) return null;
                return (
                  <tr key={dayOfWeek}>
                    <th scope="row">{WEEKDAY_LABEL[dayOfWeek]}</th>
                    <td>
                      <label>
                        <input
                          type="checkbox"
                          checked={Boolean(row.isClosed)}
                          onChange={(event) => updateRow(dayOfWeek, { isClosed: event.target.checked })}
                        />
                        Cerrado
                      </label>
                    </td>
                    <td>
                      <label htmlFor={`abre-${dayOfWeek}`} className="sr-only">
                        Abre
                      </label>
                      <select
                        id={`abre-${dayOfWeek}`}
                        value={row.opensAt ?? ''}
                        disabled={row.isClosed}
                        onChange={(event) => updateRow(dayOfWeek, { opensAt: event.target.value })}
                      >
                        <option value="" disabled>
                          Abre
                        </option>
                        {HOUR_OPTIONS.map((hour) => (
                          <option key={hour} value={hour}>
                            {hour}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <label htmlFor={`cierra-${dayOfWeek}`} className="sr-only">
                        Cierra
                      </label>
                      <select
                        id={`cierra-${dayOfWeek}`}
                        value={row.closesAt ?? ''}
                        disabled={row.isClosed}
                        onChange={(event) => updateRow(dayOfWeek, { closesAt: event.target.value })}
                      >
                        <option value="" disabled>
                          Cierra
                        </option>
                        {HOUR_OPTIONS.map((hour) => (
                          <option key={hour} value={hour}>
                            {hour}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
          {/* Where the person pressed Guardar: the choice appears in place of the button. */}
          {conflict.notice}
          {conflict.asking ? null : (
            <button type="submit" className="btn" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Guardando…' : saveMutation.uncertain ? 'Reintentar horario' : 'Guardar horario'}
            </button>
          )}
        </form>
      ) : null}
    </main>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos guardar el horario.';
}
